/**
 * Conversion Tracking & Behavior-Based Upgrade Triggers
 * Tracks user actions to show contextual upgrade prompts
 */

const STORAGE_KEY = 'shs_conversion_tracking';
const PROMO_DISMISSED_KEY = 'shs_promo_dismissed';
const ONBOARDING_COMPLETED_KEY = 'shs_onboarding_completed';

// Action thresholds for upgrade triggers
const THRESHOLDS = {
  AI_PREVIEW_VIEWS: 2,      // Show upgrade after 2 AI preview views
  TOPICS_READ: 3,           // Show upgrade after 3 topics read
  COACH_PREVIEW_VIEWS: 2,   // Show upgrade after 2 coach previews
  AUDIO_PREVIEWS: 2,        // Show upgrade after 2 audio previews
  FREE_AI_MESSAGES: 2,      // Limit free AI messages to 2 per session
};

// Get tracking data from localStorage
const getTrackingData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {
      aiPreviewViews: 0,
      topicsRead: [],
      coachPreviewViews: 0,
      audioPreviewViews: 0,
      freeAiMessages: 0,
      lastReset: new Date().toDateString(),
      upgradePromptsShown: {},
      sessionStart: Date.now(),
    };
  } catch (e) {
    return {
      aiPreviewViews: 0,
      topicsRead: [],
      coachPreviewViews: 0,
      audioPreviewViews: 0,
      freeAiMessages: 0,
      lastReset: new Date().toDateString(),
      upgradePromptsShown: {},
      sessionStart: Date.now(),
    };
  }
};

// Save tracking data
const saveTrackingData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving tracking data:', e);
  }
};

// Reset daily counters if new day
const resetDailyIfNeeded = (data) => {
  const today = new Date().toDateString();
  if (data.lastReset !== today) {
    return {
      ...data,
      freeAiMessages: 0,
      lastReset: today,
      upgradePromptsShown: {},
    };
  }
  return data;
};

/**
 * Track AI preview view
 */
export const trackAiPreviewView = () => {
  let data = getTrackingData();
  data = resetDailyIfNeeded(data);
  data.aiPreviewViews = (data.aiPreviewViews || 0) + 1;
  saveTrackingData(data);
  return data.aiPreviewViews;
};

/**
 * Track topic read
 */
export const trackTopicRead = (topicId) => {
  let data = getTrackingData();
  data = resetDailyIfNeeded(data);
  if (!data.topicsRead.includes(topicId)) {
    data.topicsRead.push(topicId);
  }
  saveTrackingData(data);
  return data.topicsRead.length;
};

/**
 * Track coach preview view
 */
export const trackCoachPreviewView = () => {
  let data = getTrackingData();
  data = resetDailyIfNeeded(data);
  data.coachPreviewViews = (data.coachPreviewViews || 0) + 1;
  saveTrackingData(data);
  return data.coachPreviewViews;
};

/**
 * Track audio preview
 */
export const trackAudioPreview = () => {
  let data = getTrackingData();
  data = resetDailyIfNeeded(data);
  data.audioPreviewViews = (data.audioPreviewViews || 0) + 1;
  saveTrackingData(data);
  return data.audioPreviewViews;
};

/**
 * Track free AI message (for soft paywall)
 */
export const trackFreeAiMessage = () => {
  let data = getTrackingData();
  data = resetDailyIfNeeded(data);
  data.freeAiMessages = (data.freeAiMessages || 0) + 1;
  saveTrackingData(data);
  return data.freeAiMessages;
};

/**
 * Get remaining free AI messages
 */
export const getRemainingFreeAiMessages = () => {
  let data = getTrackingData();
  data = resetDailyIfNeeded(data);
  const remaining = THRESHOLDS.FREE_AI_MESSAGES - (data.freeAiMessages || 0);
  return Math.max(0, remaining);
};

/**
 * Check if free AI limit reached
 */
export const isFreeAiLimitReached = () => {
  return getRemainingFreeAiMessages() <= 0;
};

/**
 * Get contextual upgrade prompt based on behavior
 */
export const getUpgradePrompt = (language = 'en') => {
  const data = getTrackingData();
  
  const prompts = {
    ai_preview: {
      en: {
        title: 'Continue your questions',
        subtitle: 'Get unlimited AI answers with AI Learning Pro',
        cta: 'Upgrade to AI Pro',
        tier: 'master_mind',
      },
      es: {
        title: 'Continúa tus preguntas',
        subtitle: 'Obtén respuestas ilimitadas con AI Learning Pro',
        cta: 'Actualizar a AI Pro',
        tier: 'master_mind',
      },
    },
    topics_read: {
      en: {
        title: 'Unlock full access',
        subtitle: 'Access all sleep topics with Full Learning Access',
        cta: 'Unlock Full Access',
        tier: 'deep_sleep',
      },
      es: {
        title: 'Desbloquea acceso completo',
        subtitle: 'Accede a todos los temas con Acceso Completo',
        cta: 'Desbloquear Acceso',
        tier: 'deep_sleep',
      },
    },
    coach_preview: {
      en: {
        title: 'Get personalized guidance',
        subtitle: 'Unlock full Sleep Coach features',
        cta: 'Start Coaching',
        tier: 'explorer',
      },
      es: {
        title: 'Obtén orientación personalizada',
        subtitle: 'Desbloquea todas las funciones de Sleep Coach',
        cta: 'Comenzar Coaching',
        tier: 'explorer',
      },
    },
    audio_preview: {
      en: {
        title: 'Continue listening',
        subtitle: 'Unlock full audio learning experience',
        cta: 'Unlock Audio',
        tier: 'explorer',
      },
      es: {
        title: 'Continúa escuchando',
        subtitle: 'Desbloquea la experiencia de audio completa',
        cta: 'Desbloquear Audio',
        tier: 'explorer',
      },
    },
    ai_limit: {
      en: {
        title: 'AI limit reached',
        subtitle: 'Continue learning with unlimited AI access',
        cta: 'Upgrade to AI Pro',
        tier: 'master_mind',
      },
      es: {
        title: 'Límite de IA alcanzado',
        subtitle: 'Continúa aprendiendo con acceso ilimitado a IA',
        cta: 'Actualizar a AI Pro',
        tier: 'master_mind',
      },
    },
  };

  // Priority order for prompts
  if (data.aiPreviewViews >= THRESHOLDS.AI_PREVIEW_VIEWS) {
    return { type: 'ai_preview', ...prompts.ai_preview[language] || prompts.ai_preview.en };
  }
  if (data.topicsRead.length >= THRESHOLDS.TOPICS_READ) {
    return { type: 'topics_read', ...prompts.topics_read[language] || prompts.topics_read.en };
  }
  if (data.coachPreviewViews >= THRESHOLDS.COACH_PREVIEW_VIEWS) {
    return { type: 'coach_preview', ...prompts.coach_preview[language] || prompts.coach_preview.en };
  }
  if (data.audioPreviewViews >= THRESHOLDS.AUDIO_PREVIEWS) {
    return { type: 'audio_preview', ...prompts.audio_preview[language] || prompts.audio_preview.en };
  }
  
  return null;
};

/**
 * Mark upgrade prompt as shown (to avoid spam)
 */
export const markPromptShown = (promptType) => {
  let data = getTrackingData();
  data.upgradePromptsShown[promptType] = Date.now();
  saveTrackingData(data);
};

/**
 * Check if prompt was recently shown (within 30 minutes)
 */
export const wasPromptRecentlyShown = (promptType) => {
  const data = getTrackingData();
  const lastShown = data.upgradePromptsShown?.[promptType];
  if (!lastShown) return false;
  const thirtyMinutes = 30 * 60 * 1000;
  return Date.now() - lastShown < thirtyMinutes;
};

/**
 * Promo banner management
 */
export const isPromoDismissed = () => {
  try {
    const dismissed = localStorage.getItem(PROMO_DISMISSED_KEY);
    if (!dismissed) return false;
    // Re-show after 7 days
    const dismissedDate = new Date(dismissed);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return dismissedDate > sevenDaysAgo;
  } catch (e) {
    return false;
  }
};

export const dismissPromo = () => {
  try {
    localStorage.setItem(PROMO_DISMISSED_KEY, new Date().toISOString());
  } catch (e) {
    console.error('Error dismissing promo:', e);
  }
};

/**
 * Onboarding management
 */
export const isOnboardingCompleted = () => {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

export const completeOnboarding = (userType) => {
  try {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    localStorage.setItem('shs_user_type', userType);
  } catch (e) {
    console.error('Error completing onboarding:', e);
  }
};

export const getUserType = () => {
  try {
    return localStorage.getItem('shs_user_type') || null;
  } catch (e) {
    return null;
  }
};

/**
 * Get recommended tier based on user type
 */
export const getRecommendedTier = (userType) => {
  const recommendations = {
    improve_sleep: {
      primary: 'explorer',
      name: 'AI Access Starter',
      reason: 'Perfect for improving your sleep habits',
    },
    learn: {
      primary: 'deep_sleep',
      name: 'Full Learning Access',
      reason: 'Best for comprehensive sleep education',
    },
    certification: {
      primary: 'professional',
      name: 'Exam Prep Pro',
      reason: 'Complete training for certification prep',
    },
  };
  return recommendations[userType] || recommendations.learn;
};

/**
 * Get conversion tracking stats (for debugging/analytics)
 */
export const getTrackingStats = () => {
  return getTrackingData();
};

/**
 * Reset all tracking data
 */
export const resetTracking = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PROMO_DISMISSED_KEY);
    localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
    localStorage.removeItem('shs_user_type');
  } catch (e) {
    console.error('Error resetting tracking:', e);
  }
};
